/**
 * Status chip for the Bexio drafts surface.
 *
 * Theme: semantic tokens + Tailwind palette colours that carry an explicit `dark:`
 * counterpart. No hardcoded hex, so light and dark both work without a retrofit rule
 * in index.css (the defect that made the old Bexio page unreadable in light mode).
 *
 * Accessibility: the state is carried by TEXT, not only by colour, and a dot is added
 * for shape redundancy.
 */
import React from "react";

import { ROW_STATUS, ROW_STATUS_LABEL } from "./bexioDraftLabels";

const NEUTRAL = "bg-muted text-muted-foreground border-input";

const TONE = {
  // A row nobody has checked yet is not a problem — it must not wear a warning
  // colour, or a fresh month would look like 14 defects.
  [ROW_STATUS.NOT_CHECKED]: NEUTRAL,
  [ROW_STATUS.CHECKING]:
    "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30",
  [ROW_STATUS.READY]:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30",
  [ROW_STATUS.BLOCKED]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30",
  [ROW_STATUS.REVIEW]:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30",
  [ROW_STATUS.CREATED]:
    "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30",
  [ROW_STATUS.FAILED]:
    "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30",
  [ROW_STATUS.CHECK_ERROR]:
    "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30",
  [ROW_STATUS.UNKNOWN]: NEUTRAL,
};

const DOT = {
  [ROW_STATUS.NOT_CHECKED]: "bg-muted-foreground",
  [ROW_STATUS.CHECKING]: "bg-blue-500",
  [ROW_STATUS.READY]: "bg-emerald-500",
  [ROW_STATUS.BLOCKED]: "bg-amber-500",
  [ROW_STATUS.REVIEW]: "bg-amber-500",
  [ROW_STATUS.CREATED]: "bg-blue-500",
  [ROW_STATUS.FAILED]: "bg-red-500",
  [ROW_STATUS.CHECK_ERROR]: "bg-red-500",
  [ROW_STATUS.UNKNOWN]: "bg-muted-foreground",
};

export default function BexioDraftStatusBadge({ status, children }) {
  const tone = TONE[status] || NEUTRAL;
  const dot = DOT[status] || DOT[ROW_STATUS.UNKNOWN];
  const label = children || ROW_STATUS_LABEL[status] || status || "—";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />
      {label}
    </span>
  );
}
